import { useCallback, useEffect, useRef } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';

import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldMark } from 'KnmPackage/MainModule/components/FieldMark/FieldMark';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { Hint } from 'CorePackage/MainModule/components/Hint';

interface ISelectTypeActionModalProps {
  name: string;
  visible: boolean;
  isSupervisor: boolean;
  onClose: () => void;
  options?: ISelectOption<string | number>[];
  onSuccess: (id: string) => void;
}

interface IForm {
  type: ISelectOption<string>;
}

export const SelectTypeActionModal = ({
  name,
  visible,
  onClose,
  options,
  onSuccess,
  isSupervisor,
}: ISelectTypeActionModalProps) => {
  const formRef = useRef<FormApi<IForm, Partial<IForm>>>();

  const handleClose = () => {
    onClose();
    formRef?.current?.reset();
  };

  const handleSave = useCallback(
    (values: IForm) => {
      onSuccess(values.type.id);
    },
    [name, formRef],
  );

  useEffect(() => {
    const optionEnabled = options?.filter(option => !option.isDisabled);

    if (formRef.current && optionEnabled && optionEnabled.length === 1 && visible) {
      formRef.current.change(`type`, optionEnabled[0] as ISelectOption<string>);
    }
  }, [formRef, visible]);

  const renderContentForm = useCallback(
    ({ handleSubmit, form, values }: FormRenderProps<IForm>) => {
      formRef.current = form;

      return (
        <>
          <ModalBody size="content" padding="small">
            <FieldBlock label={<FieldMark fieldName="type" label={'Действие по соглашению'} />}>
              <SelectInputField name={`type`} options={options} size="medium" />
            </FieldBlock>
            {isSupervisor && (
              <Hint
                margin="top"
                text="Для признания соглашения исполненным все нарушения ОТ, включенные в соглашение, должны быть устранены (устранение нарушений фиксируется в каждом отдельном нарушении)"
              />
            )}
          </ModalBody>
          <ModalActions>
            <Button text="Отменить" size="medium" type="button" onClick={handleClose} />
            <Button
              text={values.type?.id === 'terminate' ? 'Расторгнуть' : 'Продолжить'}
              color="primary"
              size="medium"
              type="button"
              onClick={handleSubmit}
            />
          </ModalActions>
        </>
      );
    },
    [handleClose, options],
  );

  return (
    <Modal visible={visible} onClose={handleClose}>
      <ModalHeader type="small" title="Работа с соглашением" />
      <Form<IForm>
        render={renderContentForm}
        onSubmit={handleSave}
        validate={values => ({
          type: makeRequired(REQUIRED_ERROR)(values.type),
        })}
        mutators={{ ...arrayMutators }}
      />
    </Modal>
  );
};
